import { formatInTimeZone } from 'date-fns-tz';
import { Seccion, SeccionPlegable, Senal } from '../../../components/DetalleExpandible';
import { etiquetaBandera } from '../../../domain/pg/catalogo';
import {
  BANDERAS_YI, CAMPOS_EXHIBICION, GATE_CERRADA, GONDOLAS_YI, GONDOLA_DE_MARCA, LLAVES_CAJAS, META_MODERNO,
  PREGUNTAS_CAMINO, PREGUNTAS_CIERRE_MOD, PREGUNTAS_ENTRADA_MOD, PREGUNTAS_VUELTA, SECCIONES_MODERNO,
  type GondolaYi,
} from '../../../domain/yichang/catalogo';
import {
  GR_OTRO, GR_PACK, brechaAdicional, caminoDeMarca, choqueObligatorio, gondolaDe, gondolaMedida,
  ladoDeExhibicion, leerEspacio, por100Moderno, presenciaModerno, puertaDe, reparto,
} from '../../../domain/yichang/moderno';
import {
  arr, en, num, obj, str, strs, NOMBRE_MARCA,
  type FichaYiModerno, type Json, type MarcaCliente, type MarcaYi,
} from '../../../domain/yichang/tipos';
import { Fila, Fotos, Objeto, PastillaPresencia, Respuestas, SIN, Valor, soles } from './Respuesta';

const TZ = 'America/Lima';

/** Los colores del pincel del formulario (`--m1` a `--m7`), en el orden de `marcas[]`. */
const COLORES = ['#C0262D', '#1d4ed8', '#0B6B5E', '#A65A0B', '#7A5AA6', '#3F6E8C', '#8A959A'];

/** Cómo se llama cada repisa según cuántas tenga el mueble, verbatim del formulario (`NIVN`). */
const REPISAS: Record<number, readonly string[]> = {
  3: ['Arriba', 'A la mano', 'Abajo'],
  4: ['Sobre la cabeza', 'A los ojos', 'A la mano', 'Piso'],
  5: ['Sobre la cabeza', 'A los ojos', 'A la mano', 'Rodilla', 'Piso'],
  6: ['Sobre la cabeza', 'A los ojos (arriba)', 'A los ojos (abajo)', 'A la mano', 'Rodilla', 'Piso'],
};

/** La cuadrícula tal como la pintó el supervisor, y el reparto que sale de ella. */
function Espacio({ gd, g }: { gd: Json; g: GondolaYi }) {
  const esp = leerEspacio(gd);
  const r = reparto(esp);
  if (esp === null || !r.medida) {
    const porque = r.motivo === 'largo_no_cuadra'
      ? 'La cuadrícula no coincide con el tamaño del mueble: cambiar módulos o repisas en el formulario la borra.'
      : r.motivo === 'solo_no_cuenta'
        ? 'Todo lo pintado quedó como «No cuenta»: no hay reparto entre marcas que calcular.'
        : 'No se pintó el espacio de esta góndola.';
    return <p className="rounded border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">{porque}</p>;
  }
  const filas = Array.from({ length: esp.filas }, (_, i) => esp.grid.slice(i * esp.cols, (i + 1) * esp.cols).split(''));
  const nombres = REPISAS[esp.filas] ?? [];
  return (
    <div className="space-y-3">
      <div className="overflow-x-auto">
        <div className="inline-block">
          {filas.map((fila, i) => (
            <div key={i} className="flex items-center gap-2">
              <span className="w-32 shrink-0 text-right text-xs text-gray-500">{nombres[i] ?? `repisa ${i + 1}`}</span>
              <div className="flex gap-0.5">
                {fila.map((d, c) => {
                  const nc = r.digitoNoCuenta !== null && Number(d) === r.digitoNoCuenta;
                  return (
                    <span
                      key={c}
                      title={d === '0' ? 'sin pintar' : esp.marcas[Number(d) - 1] ?? `dígito ${d}`}
                      className="h-5 w-4 rounded-sm border border-white"
                      style={{
                        background: d === '0' ? '#e5e7eb'
                          : nc ? 'repeating-linear-gradient(45deg,#d1d5db 0 3px,#f9fafb 3px 6px)'
                            : COLORES[Number(d) - 1] ?? '#8A959A',
                      }}
                    />
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>
      <ul className="space-y-1">
        {r.marcas.map((m) => (
          <li key={m.marca} className="flex items-center gap-2 text-sm">
            <span className="h-3 w-3 shrink-0 rounded-sm" style={{ background: COLORES[m.digito - 1] ?? '#8A959A' }} />
            <span className={`w-40 shrink-0 ${m.marca === g.marcaNom ? 'font-semibold' : ''}`}>{m.marca}</span>
            <b className="tabular-nums">{m.pct} %</b>
            <span className="text-xs text-gray-500">({m.celdas} celdas)</span>
          </li>
        ))}
      </ul>
      <p className="text-xs text-gray-500">
        El reparto va sobre las <b>{r.base}</b> celdas pintadas de la categoría
        {r.noCuenta > 0 && <> (se dejan afuera {r.noCuenta} marcadas «No cuenta»)</>}. Del mueble se pintó
        el {r.pctPintado} %. Con el redondeo la suma puede dar 99 o 101 y no se fuerza a 100.
      </p>
    </div>
  );
}

/** Un precio de 2.24 a 2.29 (o 3.26 a 3.31), con el precio por 100 g que la pantalla calcula. */
function Precio({ pb, g, grDefecto }: { pb: Json | null; g: GondolaYi; grDefecto: string | null }) {
  if (pb === null) return SIN;
  const N = g.numPrecio;
  const gramaje = str(pb.gramaje);
  const x100 = por100Moderno(pb, grDefecto);
  const conVolumen = 'volumen_precio' in pb || 'volumen_estado' in pb;
  const omitir = ['gramaje', 'gramaje_otro', 'pack_unidades', 'regular', 'regular_estado', 'oferta', 'oferta_estado',
    'tarjeta_precio', 'tarjeta_cual', 'volumen_precio', 'volumen_desde', 'volumen_estado', 'fotos_fleje',
    'fleje_en_otra_foto'];
  const nuevas = Object.fromEntries(Object.entries(pb).filter(([k]) => !omitir.includes(k)));
  return (
    <div className="space-y-2">
      <div className="grid gap-x-6 gap-y-1 sm:grid-cols-3">
        <Fila num={N.gramaje} texto="Gramaje del empaque">
          {gramaje === null ? SIN
            : gramaje === GR_PACK ? `Pack de ${num(pb.pack_unidades) ?? '—'} ${g.id === 'snacks' ? 'latas' : 'unidades'}`
              : gramaje === GR_OTRO ? (str(pb.gramaje_otro) ?? 'Otro, sin escribir') : gramaje}
        </Fila>
        <Fila num={N.regular} texto="Precio regular">
          {str(pb.regular_estado) ?? (num(pb.regular) === null ? SIN : soles(num(pb.regular)))}
        </Fila>
        <Fila num={N.oferta} texto="Precio de oferta para todos">
          {str(pb.oferta_estado) ?? (num(pb.oferta) === null ? SIN : soles(num(pb.oferta)))}
        </Fila>
        <Fila num={N.tarjeta} texto="Precio con tarjeta o app">
          {str(pb.tarjeta_cual) === null ? SIN
            : str(pb.tarjeta_cual) === 'No hay precio con tarjeta' ? 'No hay precio con tarjeta'
              : `${str(pb.tarjeta_cual)} · ${soles(num(pb.tarjeta_precio))}`}
        </Fila>
        {conVolumen && (
          <Fila num={N.volumen} texto="Precio por volumen · Makro">
            {str(pb.volumen_estado) ?? (num(pb.volumen_precio) === null ? SIN
              : `${soles(num(pb.volumen_precio))} desde ${num(pb.volumen_desde) ?? '—'} unidades`)}
          </Fila>
        )}
        <Fila num={null} texto="Por 100 g · lo calcula la pantalla">
          {x100 === null
            ? <span className="text-gray-500">no se puede calcular: falta el gramaje o el precio</span>
            : <b className="tabular-nums">{soles(x100)}</b>}
        </Fila>
      </div>
      <Fila num={N.fleje} texto="Foto del fleje">
        {pb.fleje_en_otra_foto === true
          ? 'Salió nítido en la foto de otro fleje'
          : arr(pb.fotos_fleje).length === 0 ? SIN : <Fotos lista={arr(pb.fotos_fleje)} alt="Fleje" />}
      </Fila>
      {Object.keys(nuevas).length > 0 && <Objeto o={nuevas} />}
    </div>
  );
}

function LineaBrecha({ propio, rival, p100, r100, brecha }: {
  propio: string; rival: string; p100: number | null; r100: number | null; brecha: number | null;
}) {
  if (brecha === null) {
    return <p className="text-xs text-gray-500">La brecha por 100 g no se puede calcular: falta un precio con su gramaje.</p>;
  }
  return (
    <p className="rounded bg-gray-50 px-2 py-1 text-sm">
      Por 100 g: {propio} <b className="tabular-nums">{soles(p100)}</b> contra {rival}{' '}
      <b className="tabular-nums">{soles(r100)}</b> ·{' '}
      {brecha === 0 ? 'cuestan lo mismo'
        : <>{propio} está <b>{Math.abs(brecha)} %</b> {brecha > 0 ? 'más caro' : 'más barato'}</>}
      <span className="block text-xs text-gray-500">Calculado con el precio para todos: el de oferta si lo hay.</span>
    </p>
  );
}

/** Una góndola entera, en el orden del cuestionario. */
function Gondola({ f, g }: { f: FichaYiModerno; g: GondolaYi }) {
  const gd = gondolaDe(f, g);
  const P = g.preguntas;
  const titulo = `Góndola ${g.letra} · ${g.nom.toLowerCase()} · ${g.marcaNom}`;
  if (gd === null) {
    return <Seccion titulo={titulo} ancho><p className="text-sm text-gray-600">No se registró esta góndola en la visita.</p></Seccion>;
  }
  const puerta = puertaDe(gd);
  if (!gondolaMedida(gd)) {
    return (
      <Seccion titulo={titulo} ancho>
        <div className="space-y-2">
          <Fila num={P.m1_se_pudo_medir[0]} texto={P.m1_se_pudo_medir[1]}>{puerta ?? SIN}</Fila>
          <p className="text-sm text-gray-600">
            {puerta !== null && GATE_CERRADA.includes(puerta)
              ? `Con «${puerta}» el formulario no pregunta nada más de esta góndola: no son respuestas que faltan.`
              : 'No se contestó si se podía medir, así que el resto de la góndola no se abrió.'}
          </p>
          <Fotos lista={arr(gd.m3_fotos_gondola)} alt={`Góndola de ${g.nom}`} />
        </div>
      </Seccion>
    );
  }

  const estados = obj(gd.m5_estado_sku) ?? {};
  const almacen = obj(gd.m13_almacen_segun_personal) ?? {};
  const precios = obj(gd.m10_precios) ?? {};
  const ch = obj(gd.m10_choque_obligatorio);
  const choque = choqueObligatorio(gd, g);
  const adicionales = arr(gd.m10_choques_adicionales).map(obj).filter((x): x is Json => x !== null);
  const otros = arr(gd.m10_otros_precios).map(obj).filter((x): x is Json => x !== null);
  const piezas = arr(gd.m8_piezas).map(obj).filter((x): x is Json => x !== null);
  const carteles = arr(gd.m9_carteles).map(obj).filter((x): x is Json => x !== null);

  // Lo que se dibuja con su propia pieza; el resto va por `Respuestas`, conocido o no.
  const aparte = [...META_MODERNO, 'm1_se_pudo_medir', 'm2_donde_vive_pringles', 'm3_fotos_gondola', 'm3_modulos',
    'm3_repisas', 'm3_carro_reposicion', 'm4_espacio', 'm4_frentes_latas', 'm4_frentes_estado', 'm5_estado_sku',
    'm13_almacen_segun_personal', 'm7_bloque_ninos_repisas', 'm7_corn_flakes_repisas', 'm7_bloque_kelloggs',
    'm8_sin_material', 'm8_piezas', 'm9_sin_carteles', 'm9_carteles', 'm10_precios', 'm10_choque_obligatorio',
    'm10_choques_adicionales_estado', 'm10_choques_adicionales', 'm10_otros_precios', 'm11_impulsadora',
    'm11_competencia_marca', 'm11_competencia_otra', 'm14_observacion'];
  const surtido = Object.fromEntries(Object.entries(P).filter(([k]) => /^m6_/.test(k)));
  const q = (k: string) => P[k] ?? [null, k];

  return (
    <Seccion titulo={titulo} ancho>
      <div className="space-y-4">
        <div className="grid gap-x-6 gap-y-2 sm:grid-cols-3">
          <Fila num={q('m1_se_pudo_medir')[0]} texto={q('m1_se_pudo_medir')[1]}>{puerta}</Fila>
          {'m2_donde_vive_pringles' in gd && (
            <Fila num={q('m2_donde_vive_pringles')[0]} texto={q('m2_donde_vive_pringles')[1]}>
              <Valor v={gd.m2_donde_vive_pringles} />
            </Fila>
          )}
          <Fila num={q('m3_modulos')[0]} texto={q('m3_modulos')[1]}><Valor v={gd.m3_modulos} /></Fila>
          <Fila num={q('m3_repisas')[0]} texto={q('m3_repisas')[1]}><Valor v={gd.m3_repisas} /></Fila>
          <Fila num={q('m3_carro_reposicion')[0]} texto={q('m3_carro_reposicion')[1]}><Valor v={gd.m3_carro_reposicion} /></Fila>
        </div>

        <Fila num={q('m3_fotos_gondola')[0]} texto={q('m3_fotos_gondola')[1]}>
          {arr(gd.m3_fotos_gondola).length === 0 ? SIN : <Fotos lista={arr(gd.m3_fotos_gondola)} alt={`Góndola de ${g.nom}`} />}
        </Fila>

        <Fila num={q('m4_espacio')[0]} texto={q('m4_espacio')[1]}><Espacio gd={gd} g={g} /></Fila>

        {g.frentes !== null && (
          <Fila num={q('m4_frentes_latas')[0]} texto={q('m4_frentes_latas')[1]}>
            {str(gd.m4_frentes_estado) !== null ? str(gd.m4_frentes_estado) : (
              <ul className="flex flex-wrap gap-x-5 gap-y-1">
                {g.frentes.map(([k, nom]) => {
                  const n = num(en(gd, 'm4_frentes_latas', k));
                  return <li key={k}>{nom}: <b className="tabular-nums">{n === null ? '—' : n}</b></li>;
                })}
              </ul>
            )}
          </Fila>
        )}

        {g.id === 'cereales' && (
          <div className="grid gap-x-6 gap-y-2 sm:grid-cols-3">
            {(['m7_bloque_ninos_repisas', 'm7_corn_flakes_repisas', 'm7_bloque_kelloggs'] as const).map((k) => (
              <Fila key={k} num={q(k)[0]} texto={q(k)[1]}><Valor v={gd[k]} /></Fila>
            ))}
          </div>
        )}

        <Fila num={q('m5_estado_sku')[0]} texto={q('m5_estado_sku')[1]}>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="text-left text-xs uppercase text-gray-500">
                <tr>
                  <th className="py-1 pr-3">SKU núcleo</th>
                  <th className="py-1 pr-3">Estado</th>
                  <th className="py-1 pr-3">{q('m13_almacen_segun_personal')[0]} Almacén · según el personal</th>
                  <th className="py-1 text-right">Por 100 g</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {g.nucleo.map((s) => {
                  const e = str(estados[s.k]);
                  const x100 = e === 'Está' ? por100Moderno(obj(precios[s.k]), s.gr) : null;
                  return (
                    <tr key={s.k}>
                      <td className="py-1 pr-3">{s.nom}</td>
                      <td className="py-1 pr-3">{e ?? SIN}</td>
                      <td className="py-1 pr-3 text-gray-700">
                        {e === 'Cenefa sin producto' ? (str(almacen[s.k]) ?? SIN) : <span className="text-gray-400">no aplica</span>}
                      </td>
                      <td className="py-1 text-right tabular-nums">{x100 === null ? '—' : soles(x100)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Fila>

        <SeccionPlegable
          titulo={`${q('m6_otras_presentaciones')[0]} a ${q('m6_revision_listas')[0]} Lo demás del surtido`}
          extra={<span className="text-xs text-gray-500">{str(gd.m6_revision_listas) ?? 'sin revisar'}</span>}
        >
          <Respuestas o={Object.fromEntries(Object.entries(gd).filter(([k]) => /^m6_/.test(k)))} preguntas={surtido} />
        </SeccionPlegable>

        <SeccionPlegable
          titulo={`${g.numPieza.pieza} Material publicitario`}
          extra={<span className="text-xs text-gray-500">{str(gd.m8_sin_material) ?? `${piezas.length} pieza(s)`}</span>}
        >
          {str(gd.m8_sin_material) !== null ? <p className="text-sm">{str(gd.m8_sin_material)}</p>
            : piezas.length === 0 ? SIN : (
              <ul className="space-y-2">
                {piezas.map((p, i) => (
                  <li key={i} className="rounded border border-gray-200 p-2">
                    <Objeto o={p} rotulos={{
                      pieza: [g.numPieza.pieza, '¿Qué pieza es?'],
                      marca: [g.numPieza.marca, '¿De qué marca es esta pieza?'],
                      marca_otra: [g.numPieza.marca, 'La otra marca, escrita'],
                      fotos: [g.numPieza.fotos, 'Foto de la pieza'],
                    }} omitir={str(p.marca_otra) === null ? ['marca_otra'] : []} />
                  </li>
                ))}
              </ul>
            )}
        </SeccionPlegable>

        <SeccionPlegable
          titulo={`${g.numCartel.marca} Promoción`}
          extra={<span className="text-xs text-gray-500">{str(gd.m9_sin_carteles) ?? `${carteles.length} cartel(es)`}</span>}
        >
          {str(gd.m9_sin_carteles) !== null ? <p className="text-sm">{str(gd.m9_sin_carteles)}</p>
            : carteles.length === 0 ? SIN : (
              <ul className="space-y-2">
                {carteles.map((p, i) => (
                  <li key={i} className="rounded border border-gray-200 p-2">
                    <Objeto o={p} rotulos={{
                      marca: [g.numCartel.marca, '¿De qué marca es el cartel?'],
                      marca_otra: [g.numCartel.marca, 'La otra marca, escrita'],
                      ofrece: [g.numCartel.ofrece, '¿Qué ofrece el cartel?'],
                      ofrece_otra: [g.numCartel.ofrece, 'Lo que ofrece, escrito'],
                      tarjeta: [g.numCartel.tarjeta, '¿Exige tarjeta o app de la cadena?'],
                      fotos: [g.numCartel.fotos, 'Foto del cartel'],
                    }} omitir={[
                      ...(str(p.marca_otra) === null ? ['marca_otra'] : []),
                      ...(str(p.ofrece_otra) === null ? ['ofrece_otra'] : []),
                    ]} />
                  </li>
                ))}
              </ul>
            )}
        </SeccionPlegable>

        <SeccionPlegable
          titulo={`${q('m10_precios')[0]} Precio de cada SKU núcleo`}
          extra={<span className="text-xs text-gray-500">{Object.keys(precios).length}</span>}
        >
          {Object.keys(precios).length === 0 ? <p className="text-sm text-gray-600">Ningún SKU núcleo quedó en «Está»: no hay precios que anotar.</p> : (
            <ul className="space-y-2">
              {Object.keys(precios).map((k) => {
                const s = g.nucleo.find((x) => x.k === k);
                return (
                  <li key={k} className="rounded border border-gray-200 p-2">
                    <p className="mb-1 text-xs font-semibold text-gray-700">{s?.nom ?? k}</p>
                    <Precio pb={obj(precios[k])} g={g} grDefecto={s?.gr ?? null} />
                  </li>
                );
              })}
            </ul>
          )}
        </SeccionPlegable>

        <Fila num={q('m10_choque_obligatorio')[0]} texto={q('m10_choque_obligatorio')[1]}>
          {choque.estado === 'no_se_pide' ? (
            <span className="text-gray-700">
              No se pide: {g.choque.propio} quedó en «{choque.estadoPropio}», y su ausencia ya está dicha arriba.
            </span>
          ) : choque.estado === 'sku_sin_estado' ? (
            <span className="text-gray-500">{g.choque.propio} quedó sin estado, así que el choque no se pudo pedir.</span>
          ) : choque.estado === 'sin_contestar' ? SIN
            : choque.estado === 'rival_no_esta' ? <>¿Encontraste {g.choque.rival}? <b>{choque.estadoRival}</b></>
              : (
                <div className="space-y-2">
                  <p className="text-sm">¿Encontraste {g.choque.rival}? <b>{choque.estadoRival}</b></p>
                  <div className="rounded border border-gray-200 p-2">
                    <p className="mb-1 text-xs font-semibold text-gray-700">{g.choque.rival}</p>
                    <Precio pb={obj(ch?.rival)} g={g} grDefecto={g.choque.grRival} />
                  </div>
                  <LineaBrecha propio={g.choque.propio} rival={g.choque.rival}
                    p100={choque.propio100} r100={choque.rival100} brecha={choque.brechaPct} />
                </div>
              )}
        </Fila>

        <SeccionPlegable
          titulo={`${q('m10_choques_adicionales')[0]} Choques adicionales`}
          extra={<span className="text-xs text-gray-500">{str(gd.m10_choques_adicionales_estado) ?? adicionales.length}</span>}
        >
          {str(gd.m10_choques_adicionales_estado) !== null ? <p className="text-sm">{str(gd.m10_choques_adicionales_estado)}</p>
            : adicionales.length === 0 ? SIN : (
              <ul className="space-y-2">
                {adicionales.map((x, i) => {
                  const b = brechaAdicional(gd, g, x);
                  const def = g.choquesExtra.find((p) => p.par === str(x.par));
                  return (
                    <li key={i} className="space-y-2 rounded border border-gray-200 p-2">
                      <p className="text-xs font-semibold text-gray-700">{str(x.par) ?? 'Par sin elegir'}</p>
                      <Precio pb={obj(x.rival)} g={g} grDefecto={def?.gr ?? null} />
                      {def !== undefined && (
                        <LineaBrecha propio={g.nucleo.find((s) => s.k === def.sku)?.nom ?? def.sku}
                          rival={def.rival} p100={b.propio100} r100={b.rival100} brecha={b.brechaPct} />
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
        </SeccionPlegable>

        {otros.length > 0 && (
          <SeccionPlegable
            titulo={`${q('m10_otros_precios')[0]} Otros precios que se anotaron`}
            extra={<span className="text-xs text-gray-500">{otros.length}</span>}
          >
            <ul className="space-y-2">
              {otros.map((x, i) => (
                <li key={i} className="rounded border border-gray-200 p-2">
                  <p className="mb-1 text-xs font-semibold text-gray-700">{str(x.producto) ?? 'Producto sin escribir'}</p>
                  <Precio pb={obj(x.precio)} g={g} grDefecto={null} />
                </li>
              ))}
            </ul>
          </SeccionPlegable>
        )}

        <div className="grid gap-x-6 gap-y-2 sm:grid-cols-3">
          <Fila num={q('m11_impulsadora')[0]} texto={q('m11_impulsadora')[1]}><Valor v={gd.m11_impulsadora} /></Fila>
          {strs(gd.m11_impulsadora).includes('Impulsadora o degustación de la competencia') && (
            <Fila num={q('m11_competencia_marca')[0]} texto={q('m11_competencia_marca')[1]}>
              <Valor v={gd.m11_competencia_marca} />
              {str(gd.m11_competencia_otra) !== null && <span className="block text-xs">{str(gd.m11_competencia_otra)}</span>}
            </Fila>
          )}
          <Fila num={q('m14_observacion')[0]} texto={q('m14_observacion')[1]}><Valor v={gd.m14_observacion} /></Fila>
        </div>

        <Respuestas o={Object.fromEntries(Object.entries(gd).filter(([k]) => !aparte.includes(k) && !/^m6_/.test(k)))} preguntas={{}} />
      </div>
    </Seccion>
  );
}

function Exhibiciones({ lista }: { lista: readonly Json[] }) {
  if (lista.length === 0) return <p className="text-sm text-gray-600">Ninguna registrada.</p>;
  return (
    <ul className="space-y-2">
      {lista.map((e, i) => (
        <li key={i} className="rounded border border-gray-200 p-2">
          <Objeto o={e} rotulos={CAMPOS_EXHIBICION} omitir={str(e.marca_otra) === null ? ['marca_otra'] : []} />
        </li>
      ))}
    </ul>
  );
}

/**
 * El súper detalle de una ficha de canal moderno.
 *
 * Con **las dos marcas** se ve la ficha entera, en el orden del recorrido. Con **una marca**, el
 * detalle se enfoca en su parte: su góndola, la publicidad del camino que la nombra, sus exhibiciones
 * y —para Pringles— la zona de cajas. Lo que es de la visita y no de una marca (el cierre, las
 * exhibiciones que no son de ninguna de las dos categorías) queda en una sección plegada al final: no
 * se esconde, pero no compite con lo que se está mirando.
 */
export default function DetalleYiModerno({ ficha: f, marca }: { ficha: FichaYiModerno; marca: MarcaYi }) {
  const j = f.ficha;
  const vuelta = obj(j.vuelta);
  const exh = arr(vuelta?.c1_exhibiciones).map(obj).filter((x): x is Json => x !== null);
  const cajas = vuelta === null ? null
    : Object.fromEntries(Object.entries(vuelta).filter(([k]) => LLAVES_CAJAS.includes(k)));
  const conocidas = new Set([...SECCIONES_MODERNO.map((s) => s.id), ...META_MODERNO]);
  const otrasSecciones = Object.fromEntries(Object.entries(j).filter(([k]) => !conocidas.has(k)));
  const enfocada: MarcaCliente | null = marca === 'las_dos' ? null : marca;
  const gondolas = enfocada === null ? GONDOLAS_YI : [GONDOLA_DE_MARCA[enfocada]];
  const vueltaSinCajasNiExh = vuelta === null ? null
    : Object.fromEntries(Object.entries(vuelta).filter(([k]) => !LLAVES_CAJAS.includes(k) && k !== 'c1_exhibiciones'));

  return (
    <>
      <Seccion titulo="La visita" ancho>
        <div className="space-y-3">
          <div className="grid gap-x-6 gap-y-2 sm:grid-cols-3">
            {/* Tienda, cadena y supervisor salen de la entrada (0.1 a 0.4), con su número, más abajo.
                Solo se repiten acá si la ficha no trae la entrada: nunca se quedan sin mostrar. */}
            {obj(j.entrada) === null && <>
            <Fila num={null} texto="Tienda">
              {f.tiendaTexto ?? SIN}
              {f.flags.includes('tienda_fuera_de_maestra') && <> <Senal>escrita a mano</Senal></>}
            </Fila>
            <Fila num={null} texto="Cadena · formato">{[f.cadena, f.formato].filter(Boolean).join(' · ') || SIN}</Fila>
            <Fila num={null} texto="Supervisor">{f.supervisor ?? SIN}</Fila>
            </>}
            <Fila num={null} texto="Coordenadas">
              {f.lat === null || f.lng === null ? <Senal tono="neutro">sin GPS</Senal> : (
                <a className="text-blue-700 underline" target="_blank" rel="noreferrer"
                  href={`https://www.google.com/maps?q=${f.lat},${f.lng}`}>
                  {f.lat.toFixed(5)}, {f.lng.toFixed(5)}
                </a>
              )}
            </Fila>
            <Fila num={null} texto="Se envió">{f.origin === 'offline_sync' ? 'sin señal, desde la cola' : 'con señal'}</Fila>
            <Fila num={null} texto="Llegó al servidor">
              {f.registradoEn === null ? SIN : formatInTimeZone(new Date(f.registradoEn), TZ, 'dd/MM/yyyy HH:mm')}
              {f.duracionSeg !== null && <span className="block text-xs text-gray-500">la ficha tomó {Math.round(f.duracionSeg / 60)} min</span>}
            </Fila>
          </div>
          {f.flags.length > 0 && (
            <div className="flex flex-wrap gap-1">
              {f.flags.map((x) => { const e = etiquetaBandera(x, BANDERAS_YI); return <Senal key={x} tono={e.tono}>{e.label}</Senal>; })}
            </div>
          )}
          {enfocada !== null && (
            <p className="text-sm">
              {NOMBRE_MARCA[enfocada]} en esta tienda: <PastillaPresencia p={presenciaModerno(f, enfocada)} />
            </p>
          )}
          <Respuestas o={obj(j.entrada)} preguntas={PREGUNTAS_ENTRADA_MOD} />
        </div>
      </Seccion>

      {enfocada === null ? (
        <Seccion titulo="Camino al pasillo" ancho>
          <Respuestas o={obj(j.camino)} preguntas={PREGUNTAS_CAMINO} ancho />
        </Seccion>
      ) : (
        <Seccion titulo="Camino al pasillo" ancho>
          <div className="grid gap-x-6 gap-y-2 sm:grid-cols-2">
            <Fila num="1.1" texto={`¿Viste publicidad de ${NOMBRE_MARCA[enfocada]} desde la entrada hasta el pasillo?`}>
              {(() => {
                const c = caminoDeMarca(f, enfocada);
                return c === null ? SIN : c === 'si' ? 'Sí' : 'No';
              })()}
            </Fila>
            {caminoDeMarca(f, enfocada) === 'si' && (
              <Fila num="1.2" texto="Describe lo que viste">
                <Valor v={en(j, 'camino', 'm0_publicidad_detalle')} />
              </Fila>
            )}
          </div>
        </Seccion>
      )}

      {gondolas.map((g) => <Gondola key={g.id} f={f} g={g} />)}

      <Seccion titulo="Vuelta por la tienda" ancho>
        <div className="space-y-3">
          <Fila num="4.1" texto={enfocada === null ? 'Exhibiciones fuera del pasillo'
            : `Exhibiciones fuera del pasillo · del lado de ${NOMBRE_MARCA[enfocada]} (la marca y su competencia)`}>
            <Exhibiciones lista={enfocada === null ? exh : exh.filter((e) => ladoDeExhibicion(e) === enfocada)} />
          </Fila>
          <Respuestas o={vueltaSinCajasNiExh} preguntas={PREGUNTAS_VUELTA} />
          {(enfocada === null || enfocada === 'pringles') && (
            <div>
              <h5 className="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-500">Zona de cajas · Pringles</h5>
              <Respuestas o={cajas} preguntas={PREGUNTAS_VUELTA} />
            </div>
          )}
        </div>
      </Seccion>

      {enfocada === null ? (
        <Seccion titulo="Cerrar la visita" ancho>
          <Respuestas o={obj(j.cierre)} preguntas={PREGUNTAS_CIERRE_MOD} />
        </Seccion>
      ) : (
        <SeccionPlegable titulo="Lo demás de la visita · no es de una marca" ancho>
          <div className="space-y-3">
            <Respuestas o={obj(j.cierre)} preguntas={PREGUNTAS_CIERRE_MOD} />
            <Fila num="4.1" texto="Exhibiciones que no son de ninguna de las dos categorías">
              <Exhibiciones lista={exh.filter((e) => ladoDeExhibicion(e) === null)} />
            </Fila>
          </div>
        </SeccionPlegable>
      )}

      {Object.keys(otrasSecciones).length > 0 && (
        <Seccion titulo="Otras respuestas" ancho>
          <Respuestas o={otrasSecciones} preguntas={{}} />
        </Seccion>
      )}
    </>
  );
}
